import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DatePicker,
  LocalizationProvider
} from "@mui/x-date-pickers";
import { useTranslation } from 'react-i18next';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from 'dayjs';
import {
  updateSecretAsync,
  selectSecretsLoading,
  selectSecretsError,
  selectUpdateSuccess,
  selectUpdateMessage,
  clearUpdateStatus, selectSecrets
} from '../redux/getSecrets/reducer';
import { AppDispatch } from '../redux/store';
import '../styles/css/secret.css';
import brand from "../brand";

interface UpdateSecretModalProps {
  onClose: () => void;
}

const UpdateSecret = ({ onClose }: UpdateSecretModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const isLoading = useSelector(selectSecretsLoading);
  const error = useSelector(selectSecretsError);
  const updateSuccess = useSelector(selectUpdateSuccess);
  const updateMessage = useSelector(selectUpdateMessage);
  const secrets = useSelector(selectSecrets);

  const [credentialType, setCredentialType] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [expiryDate, setExpiryDate] = useState<Dayjs | null>(dayjs());

  useEffect(() => {
    return () => {
      dispatch(clearUpdateStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    if (updateSuccess) {
      const timer = setTimeout(() => {
        onClose();
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [updateSuccess, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (credentialType === '') {
      return;
    }

    const payload = {
      credentialType,
      ...(credentialType === 'graphapi' ? {
        accessKey,
        secretKey,
        expiryDate: expiryDate ? expiryDate.format('YYYY-MM-DD') : undefined
      } : {})
    };

    // Dispatch update action
    dispatch(updateSecretAsync(payload));
  };

  const errorMessage = error && (error.error || '');

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header textColor" style={{fontFamily: brand.bodyFont}}>
          <h3>{t('Update Credentials')}</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{fontFamily: brand.bodyFont}}>
            <div className="form-group">
              <select
                id="credentialType"
                value={credentialType}
                onChange={(e) => setCredentialType(e.target.value)}
                className="credential-select"
                aria-label="Credential Type"
                disabled={isLoading}
                style={{fontFamily: brand.bodyFont}}
              >
                <option value="">{t('Select Credential Type *')}</option>
                { secrets.map((secret) => (
                  <option value={secret.secret_name}>{secret.secret_name}</option>
                ))}
              </select>
            </div>

            {credentialType === 'graphapi' ? (
              <>
                <div className="form-group">
                  <input
                    id="accessKey"
                    type="text"
                    value={accessKey}
                    onChange={(e) => setAccessKey(e.target.value)}
                    placeholder={t("Access Key *")}
                    required
                    className="form-input"
                    aria-label="Access Key"
                    disabled={isLoading}
                    style={{fontFamily: brand.bodyFont}}
                  />
                </div>
                <div className="form-group">
                  <input
                    id="secretKey"
                    type="password"
                    value={secretKey}
                    onChange={(e) => setSecretKey(e.target.value)}
                    placeholder={t("Secret Key *")}
                    required
                    className="form-input"
                    aria-label="Secret Key"
                    disabled={isLoading}
                    style={{fontFamily: brand.bodyFont}}
                  />
                </div>
                <div className="form-group date-picker-container" style={{fontFamily: brand.bodyFont}}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label={t("Expiry Date")}
                      value={expiryDate}
                      onChange={(newValue) => setExpiryDate(newValue)}
                      format="MM/DD/YYYY"
                      disabled={isLoading}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          variant: "outlined",
                          className: "mui-date-picker",
                          inputProps: {
                            "aria-label": "Expiry Date"
                          },
                          
                        }
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </>
            ) : credentialType === 'afi' ? (
              <div className="form-group">
                <input
                  id="secretKey"
                  type="password"
                  value={secretKey}
                  onChange={(e) => setSecretKey(e.target.value)}
                  placeholder={t("Secret Key *")}
                  required
                  className="form-input"
                  aria-label="Secret Key"
                  disabled={isLoading}
                  style={{fontFamily: brand.bodyFont}}
                />
              </div>
            ) : credentialType ? (
              <div className="info-message" style={{fontFamily: brand.bodyFont}}>
                <p>{t('This will update the credentials.')}</p>
                <p>{t('Please click "Request Update" to continue.')}</p>
              </div>
            ) : null}

            {errorMessage && !updateSuccess && (
              <div className="error-message" style={{fontFamily: brand.bodyFont}}>
                <p>{t(errorMessage)}</p>
              </div>
            )}

            {updateSuccess && updateMessage && (
              <div className="success-message" style={{fontFamily: brand.bodyFont}}>
                <p>{t(updateMessage)}</p>
              </div>
            )}
          </div>
          <div className="modal-footer" style={{fontFamily: brand.bodyFont}}>
            <button
              type="button"
              className="cancel-button"
              onClick={onClose}
              disabled={isLoading}
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              className="submit-button formButton"
              disabled={!credentialType || isLoading}
            >
              {isLoading ? t('Processing...') :
                !credentialType ? t('Submit') :
                credentialType === 'graphapi' ? t('Update') : t('Request Update')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateSecret;